import React, { useCallback, useEffect, useState } from 'react';
import { SControlButton, SVolumeWrapper, SVolumeSlider } from './styled';

interface IProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
}

const VolumeControl: React.FC<IProps> = ({ videoRef }) => {
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);

  // Sync state from video element (e.g. browser-restored volume)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const handleVolumeChange = () => {
      setVolume(video.volume);
      setMuted(video.muted);
    };

    video.addEventListener('volumechange', handleVolumeChange);
    // Catch-up: if the element was already muted/quiet before this mounted, no further
    // volumechange fires and the icon would lie.
    handleVolumeChange();

    return () => video.removeEventListener('volumechange', handleVolumeChange);
  }, [videoRef]);

  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = parseFloat(e.target.value);
      if (videoRef.current) {
        videoRef.current.volume = val;
        videoRef.current.muted = val === 0;
      }
    },
    [videoRef]
  );

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    // Read the element, not React state, so unmuting works before state catches up.
    const next = !video.muted;
    video.muted = next;
    if (!next && video.volume === 0) {
      video.volume = 1;
    }
  }, [videoRef]);

  return (
    <SVolumeWrapper>
      <SControlButton onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'}>
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          {muted || volume === 0 ? (
            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
          ) : volume < 0.5 ? (
            <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" />
          ) : (
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
          )}
        </svg>
      </SControlButton>
      <SVolumeSlider
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={muted ? 0 : volume}
        onChange={handleSliderChange}
        aria-label="Volume"
      />
    </SVolumeWrapper>
  );
};

export default VolumeControl;
